"use client";

import {
  Sidebar,
  SidebarLayout,
  type SidebarNavigationGroup,
} from "@/src/components/navigation/sidebar";
import Logo from "@/src/components/information/brand-logo";
import { Button, Switch, Text } from "@/src/components";
import { Moon, Sun, PanelLeftClose, PanelLeft as PanelLeftIcon } from "lucide-react";
import {
  BarChart01,
  Calendar,
  CheckSquare,
  ChevronRight,
  FilterLines,
  HelpCircle,
  Image03,
  LayoutRight,
  List,
  MessageChatSquare,
  Rows01,
  SearchMd,
  Square,
  Table,
  Toggle01Left,
  Type01,
  Type02,
  User01
} from "@untitledui/icons";
import {
  Accessibility,
  Bell,
  Book,
  Bot,
  Box,
  CalendarRange,
  ChevronsUpDown,
  CircleAlert,
  Columns,
  Command,
  Container,
  FileText,
  FormInput,
  Gauge,
  GitBranch,
  GripVertical,
  Hash,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  LayoutTemplate,
  ListTree,
  Loader2,
  Maximize2,
  MoreHorizontal,
  Palette,
  PanelLeft,
  PanelRightOpen,
  PanelTop,
  PieChart,
  Play,
  Radio,
  RectangleHorizontal,
  ScrollText,
  SeparatorHorizontal,
  Settings,
  Sparkles,
  Star,
  StickyNote,
  Paintbrush,
  Tag,
  ToggleLeft,
  Type,
  Zap,
  Plug,
  ClipboardCheck,
  FileSearch
} from "lucide-react";
import * as React from "react";
import { Link, Outlet } from "react-router-dom";

const docsNavigation: SidebarNavigationGroup[] = [
  // ============================================
  // LEARN - Educational content
  // ============================================
  {
    title: "Learn",
    subsections: [
      {
        title: "Getting Started",
        items: [
          { label: "Introduction", href: "/docs", icon: <Book className="h-4 w-4" /> },
          { label: "Installation", href: "/docs/installation", icon: <Zap className="h-4 w-4" /> },
        ],
      },
      {
        title: "Foundations",
        items: [
          { label: "Design Tokens", href: "/docs/tokens/colors", icon: <Palette className="h-4 w-4" /> },
          { label: "Themes", href: "/docs/themes", icon: <Paintbrush className="h-4 w-4" /> },
          { label: "Typography", href: "/docs/tokens/typography", icon: <Type01 className="h-4 w-4" /> },
          { label: "Spacing", href: "/docs/tokens/spacing", icon: <LayoutGrid className="h-4 w-4" /> },
          { label: "Layout", href: "/docs/foundations/layout", icon: <Layers className="h-4 w-4" /> },
          { label: "Accessibility", href: "/docs/accessibility", icon: <Accessibility className="h-4 w-4" /> },
        ],
      },
      {
        title: "Brand",
        items: [
          { label: "Guidelines", href: "/docs/brand", icon: <Star className="h-4 w-4" /> },
          { label: "Logo", href: "/docs/brand/logo", icon: <Image03 className="h-4 w-4" /> },
        ],
      },
    ],
  },
  // ============================================
  // COMPONENTS - 5 clear categories (Input, Navigation, Information, Container, Layout)
  // ============================================
  {
    title: "Components",
    subsections: [
      {
        title: "Input",
        items: [
          { label: "Button", href: "/docs/components/button", icon: <Square className="h-4 w-4" /> },
          { label: "Input", href: "/docs/components/input", icon: <Type02 className="h-4 w-4" /> },
          { label: "Textarea", href: "/docs/components/textarea", icon: <FileText className="h-4 w-4" /> },
          { label: "Select", href: "/docs/components/select", icon: <ChevronsUpDown className="h-4 w-4" /> },
          { label: "Checkbox", href: "/docs/components/checkbox", icon: <CheckSquare className="h-4 w-4" /> },
          { label: "Radio Group", href: "/docs/components/radio-group", icon: <Radio className="h-4 w-4" /> },
          { label: "Switch", href: "/docs/components/switch", icon: <Toggle01Left className="h-4 w-4" /> },
          { label: "Calendar", href: "/docs/components/calendar", icon: <Calendar className="h-4 w-4" /> },
          { label: "Search Box", href: "/docs/components/search-box", icon: <SearchMd className="h-4 w-4" /> },
          { label: "Command", href: "/docs/components/command", icon: <Command className="h-4 w-4" /> },
          { label: "Combobox", href: "/docs/components/combobox", icon: <ChevronsUpDown className="h-4 w-4" /> },
          { label: "Label", href: "/docs/components/label", icon: <Type className="h-4 w-4" /> },
          { label: "Form Field", href: "/docs/components/form-field", icon: <FormInput className="h-4 w-4" /> },
          { label: "Pills", href: "/docs/components/pills", icon: <Tag className="h-4 w-4" /> },
          { label: "Number Input", href: "/docs/components/number-input", icon: <Hash className="h-4 w-4" /> },
          { label: "Toggle", href: "/docs/components/toggle", icon: <ToggleLeft className="h-4 w-4" /> },
          { label: "Date Picker", href: "/docs/blocks/date-picker", icon: <Calendar className="h-4 w-4" /> },
          { label: "Date Range Picker", href: "/docs/blocks/date-range-picker", icon: <CalendarRange className="h-4 w-4" /> },
          { label: "Filter Builder", href: "/docs/blocks/filter-builder", icon: <FilterLines className="h-4 w-4" /> },
        ],
      },
      {
        title: "Navigation",
        items: [
          { label: "Tabs", href: "/docs/components/tabs", icon: <Columns className="h-4 w-4" /> },
          { label: "Breadcrumb", href: "/docs/components/breadcrumb", icon: <ChevronRight className="h-4 w-4" /> },
          { label: "Sidebar", href: "/docs/blocks/sidebar", icon: <PanelLeft className="h-4 w-4" /> },
          { label: "Topbar", href: "/docs/blocks/topbar", icon: <PanelTop className="h-4 w-4" /> },
          { label: "Horizontal Tabs", href: "/docs/blocks/horizontal-tabs", icon: <Columns className="h-4 w-4" /> },
          { label: "Pagination", href: "/docs/blocks/pagination", icon: <MoreHorizontal className="h-4 w-4" /> },
          { label: "Stepper", href: "/docs/components/stepper", icon: <GitBranch className="h-4 w-4" /> },
          { label: "Tree Menu", href: "/docs/components/tree-menu", icon: <ListTree className="h-4 w-4" /> },
        ],
      },
      {
        title: "Information",
        items: [
          { label: "Text", href: "/docs/components/text", icon: <Type className="h-4 w-4" /> },
          { label: "Badge", href: "/docs/components/badge", icon: <RectangleHorizontal className="h-4 w-4" /> },
          { label: "Avatar", href: "/docs/components/avatar", icon: <User01 className="h-4 w-4" /> },
          { label: "Alert", href: "/docs/components/alert", icon: <CircleAlert className="h-4 w-4" /> },
          { label: "Tooltip", href: "/docs/components/tooltip", icon: <HelpCircle className="h-4 w-4" /> },
          { label: "Toast", href: "/docs/components/toast", icon: <Bell className="h-4 w-4" /> },
          { label: "Progress", href: "/docs/components/progress", icon: <BarChart01 className="h-4 w-4" /> },
          { label: "Spinner", href: "/docs/components/spinner", icon: <Loader2 className="h-4 w-4" /> },
          { label: "Skeleton", href: "/docs/components/skeleton", icon: <Box className="h-4 w-4" /> },
          { label: "Empty State", href: "/docs/components/empty-state", icon: <Box className="h-4 w-4" /> },
          { label: "AG Grid", href: "/docs/blocks/ag-grid", icon: <Table className="h-4 w-4" /> },
          { label: "Data Table", href: "/docs/blocks/data-table", icon: <Table className="h-4 w-4" /> },
          { label: "Listing", href: "/docs/blocks/listing", icon: <List className="h-4 w-4" /> },
        ],
      },
      {
        title: "Container",
        items: [
          { label: "Card", href: "/docs/components/card", icon: <StickyNote className="h-4 w-4" /> },
          { label: "Dialog", href: "/docs/components/dialog", icon: <Maximize2 className="h-4 w-4" /> },
          { label: "Sheet", href: "/docs/components/sheet", icon: <PanelLeft className="h-4 w-4" /> },
          { label: "Popover", href: "/docs/components/popover", icon: <MessageChatSquare className="h-4 w-4" /> },
          { label: "Dropdown Menu", href: "/docs/components/dropdown-menu", icon: <MoreHorizontal className="h-4 w-4" /> },
          { label: "Accordion", href: "/docs/components/accordion", icon: <ChevronsUpDown className="h-4 w-4" /> },
          { label: "Scroll Area", href: "/docs/components/scroll-area", icon: <ScrollText className="h-4 w-4" /> },
          { label: "Separator", href: "/docs/components/separator", icon: <SeparatorHorizontal className="h-4 w-4" /> },
          { label: "Modal", href: "/docs/blocks/modal", icon: <Maximize2 className="h-4 w-4" /> },
          { label: "Side Panel", href: "/docs/components/side-panel", icon: <PanelRightOpen className="h-4 w-4" /> },
        ],
      },
      {
        title: "Layout",
        items: [
          { label: "Scaffold", href: "/docs/components/scaffold", icon: <LayoutTemplate className="h-4 w-4" /> },
          { label: "Page Container", href: "/docs/components/page-container", icon: <Container className="h-4 w-4" /> },
          { label: "Section", href: "/docs/components/section", icon: <Rows01 className="h-4 w-4" /> },
          { label: "Stack", href: "/docs/components/stack", icon: <GripVertical className="h-4 w-4" /> },
          { label: "Page Header", href: "/docs/blocks/page-header", icon: <PanelTop className="h-4 w-4" /> },
        ],
      },
    ],
  },
  // ============================================
  // CHARTS - ECharts-based data visualization
  // ============================================
  {
    title: "Charts",
    subsections: [
      {
        title: "Charts",
        items: [
          { label: "Overview", href: "/docs/charts", icon: <PieChart className="h-4 w-4" /> },
          { label: "Bar Chart", href: "/docs/charts/bar", icon: <BarChart01 className="h-4 w-4" /> },
          { label: "Line Chart", href: "/docs/charts/line", icon: <BarChart01 className="h-4 w-4" /> },
          { label: "Area Chart", href: "/docs/charts/area", icon: <BarChart01 className="h-4 w-4" /> },
          { label: "Pie Chart", href: "/docs/charts/pie", icon: <PieChart className="h-4 w-4" /> },
          { label: "Donut Chart", href: "/docs/charts/donut", icon: <PieChart className="h-4 w-4" /> },
          { label: "Waterfall Chart", href: "/docs/charts/waterfall", icon: <BarChart01 className="h-4 w-4" /> },
          { label: "Gauge Chart", href: "/docs/charts/gauge", icon: <Gauge className="h-4 w-4" /> },
          { label: "Treemap Chart", href: "/docs/charts/treemap", icon: <LayoutGrid className="h-4 w-4" /> },
          { label: "Dashboard Grid", href: "/docs/charts/dashboard-grid", icon: <LayoutDashboard className="h-4 w-4" /> },
        ],
      },
    ],
  },
  // ============================================
  // PAGE TEMPLATES - Pre-built pages with controller pattern
  // ============================================
  {
    title: "Page Templates",
    subsections: [
      {
        title: "Templates",
        items: [
          { label: "Dashboard", href: "/docs/templates/dashboard", icon: <LayoutDashboard className="h-4 w-4" /> },
          { label: "Listing", href: "/docs/templates/listing", icon: <List className="h-4 w-4" /> },
          { label: "Detail", href: "/docs/templates/detail", icon: <FileSearch className="h-4 w-4" /> },
          { label: "Approval", href: "/docs/templates/approval", icon: <ClipboardCheck className="h-4 w-4" /> },
          { label: "Settings", href: "/docs/templates/settings", icon: <Settings className="h-4 w-4" /> },
        ],
      },
    ],
  },
  // ============================================
  // MOTION - Animation system
  // ============================================
  {
    title: "Motion",
    subsections: [
      {
        title: "Animation",
        items: [
          { label: "Motion System", href: "/docs/motion", icon: <Play className="h-4 w-4" /> },
          { label: "Micro-interactions", href: "/docs/motion/micro", icon: <Sparkles className="h-4 w-4" /> },
        ],
      },
    ],
  },
  // ============================================
  // UTILITIES - Hooks and helpers
  // ============================================
  {
    title: "Utilities",
    subsections: [
      {
        title: "Hooks",
        items: [
          { label: "All Hooks", href: "/docs/hooks", icon: <Plug className="h-4 w-4" /> },
        ],
      },
    ],
  },
  // ============================================
  // AI ASSISTANT - Instructions for AI tools
  // ============================================
  {
    title: "AI Assistant",
    subsections: [
      {
        title: "Instructions",
        items: [
          { label: "Boilerplate Guide", href: "/docs/ai/boilerplate", icon: <Bot className="h-4 w-4" /> },
          { label: "AI Instructions", href: "/docs/ai/instructions", icon: <Bot className="h-4 w-4" /> },
        ],
      },
    ],
  },
];

export default function DocsLayout({
  children,
}: {
  children?: React.ReactNode;
}) {
  const [collapsed, setCollapsed] = React.useState(() => {
    try {
      return localStorage.getItem("dl-sidebar-collapsed") === "true";
    } catch {
      return false;
    }
  });
  const [popupState, setPopupState] = React.useState<"hidden" | "visible" | "closing">("hidden");
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const closeAnimationRef = React.useRef<NodeJS.Timeout | null>(null);
  const [isDark, setIsDark] = React.useState(() => {
    try {
      const stored = localStorage.getItem("dl-dark-mode");
      if (stored !== null) {
        const dark = stored === "true";
        // Sync DOM on mount
        if (dark) document.documentElement.classList.add("dark");
        else document.documentElement.classList.remove("dark");
        return dark;
      }
    } catch {}
    if (typeof window !== "undefined") {
      return document.documentElement.classList.contains("dark");
    }
    return false;
  });

  // Persist collapsed state
  const handleCollapsedChange = React.useCallback((value: boolean) => {
    setCollapsed(value);
    try { localStorage.setItem("dl-sidebar-collapsed", String(value)); } catch {}
  }, []);

  const toggleDarkMode = (checked: boolean) => {
    setIsDark(checked);
    try { localStorage.setItem("dl-dark-mode", String(checked)); } catch {}
    if (checked) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  // Hover handlers for popup sidebar with 3-second delay
  const handlePopupMouseEnter = React.useCallback(() => {
    if (!collapsed) return;
    // Clear any pending hide timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    // Clear any pending close animation
    if (closeAnimationRef.current) {
      clearTimeout(closeAnimationRef.current);
      closeAnimationRef.current = null;
    }
    setPopupState("visible");
  }, [collapsed]);

  const handlePopupMouseLeave = React.useCallback(() => {
    if (!collapsed) return;
    // Start 3-second delay before hiding
    hoverTimeoutRef.current = setTimeout(() => {
      // Start closing animation
      setPopupState("closing");
      // After animation completes, hide completely
      closeAnimationRef.current = setTimeout(() => {
        setPopupState("hidden");
        closeAnimationRef.current = null;
      }, 200); // Match animation duration
      hoverTimeoutRef.current = null;
    }, 1500);
  }, [collapsed]);

  // Cleanup timeouts on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (closeAnimationRef.current) {
        clearTimeout(closeAnimationRef.current);
      }
    };
  }, []);

  // Hide popup when expanding sidebar
  React.useEffect(() => {
    if (!collapsed) {
      setPopupState("hidden");
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      if (closeAnimationRef.current) {
        clearTimeout(closeAnimationRef.current);
        closeAnimationRef.current = null;
      }
    }
  }, [collapsed]);

  // Shared footer component
  const sidebarFooter = (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {isDark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          <span>Dark Mode</span>
        </div>
        <Switch checked={isDark} onCheckedChange={toggleDarkMode} />
      </div>
      <Link to="/" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">
        ← Back to Showcase
      </Link>
    </div>
  );

  return (
    <SidebarLayout
      isCollapsed={collapsed}
      collapsedTopbar={
        <>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleCollapsedChange(false)}
            onMouseEnter={handlePopupMouseEnter}
            onMouseLeave={handlePopupMouseLeave}
            className="h-8 w-8 shrink-0"
            aria-label="Expand sidebar"
          >
            <PanelLeftIcon className="h-4 w-4" />
          </Button>
          <Link to="/" className="flex items-center gap-2">
            <Logo size={28} />
            <Text as="div" className="text-h5 font-bold">
              Dream Light
            </Text>
          </Link>
        </>
      }
      popupSidebar={
        <Sidebar
          position="left"
          width={300}
          collapsed={false}
          hideToggle
          searchable={false}
          footer={sidebarFooter}
          navigation={docsNavigation}
        />
      }
      popupState={popupState}
      onPopupMouseEnter={handlePopupMouseEnter}
      onPopupMouseLeave={handlePopupMouseLeave}
      sidebar={
        <Sidebar
          position="left"
          width={300}
          collapsed={collapsed}
          onCollapsedChange={handleCollapsedChange}
          searchable={false}
          searchPlaceholder="Search docs..."
          logo={<Logo size={28} />}
          header={
            <Link to="/">
              <Text as="div" className="text-h5 font-bold">
                Dream Light
              </Text>
            </Link>
          }
          footer={sidebarFooter}
          navigation={docsNavigation}
        />
      }
    >
      <main className="flex-1 overflow-auto h-full px-theme-8 py-theme-6">
          <Outlet />
      </main>
    </SidebarLayout>
  );
}
